import { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface CBPDocSlot {
  id: 'form7501' | 'duty_payment' | 'invoice_form4811';
  label: string;
  description: string;
  detail: string;
  icon: string;
  categoryKey: string;
}

interface UploadedFile {
  id: string;
  file_name: string;
  file_size: number;
  uploaded_at: string;
}

interface Props {
  clientId: string;
  userId: string;
  alreadySubmitted: boolean;
  onAllSubmitted: (submittedAt: string) => void;
}

const SLOTS: CBPDocSlot[] = [
  {
    id: 'form7501',
    label: 'CBP Form 7501 — Entry Summary',
    description: 'Original Entry Summary for the shipment in question',
    detail: 'Filed with CBP at time of entry. Held by the importer of record or the customs broker that filed the entry.',
    icon: 'ri-file-paper-2-line',
    categoryKey: 'cbp:form7501',
  },
];

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CBPDocumentGathering({ clientId, userId, alreadySubmitted, onAllSubmitted }: Props) {
  const [uploadedMap, setUploadedMap] = useState<Record<string, UploadedFile | null>>({
    form7501: null,
  });
  const [uploading, setUploading] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState(!alreadySubmitted);
  const [markingDone, setMarkingDone] = useState(false);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    if (!clientId) return;
    loadExistingDocs();
  }, [clientId]);

  const loadExistingDocs = async () => {
    const { data } = await supabase
      .from('case_documents')
      .select('id, file_name, file_size, uploaded_at, description')
      .eq('client_id', clientId)
      .order('uploaded_at', { ascending: false });

    if (!data) return;

    const map: Record<string, UploadedFile | null> = {
      form7501: null,
    };

    SLOTS.forEach((slot) => {
      const found = data.find((d) => d.description === slot.categoryKey);
      if (found) {
        map[slot.id] = {
          id: found.id,
          file_name: found.file_name,
          file_size: found.file_size,
          uploaded_at: found.uploaded_at,
        };
      }
    });

    setUploadedMap(map);
  };

  const handleFileChange = async (slotId: string, categoryKey: string, file: File | null) => {
    if (!file) return;
    setErrors((prev) => ({ ...prev, [slotId]: '' }));

    if (!ALLOWED_TYPES.includes(file.type)) {
      setErrors((prev) => ({ ...prev, [slotId]: 'Unsupported file type. Use PDF, JPG, PNG, DOCX, or TXT.' }));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setErrors((prev) => ({ ...prev, [slotId]: 'File exceeds 10 MB limit.' }));
      return;
    }

    setUploading(slotId);
    try {
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${userId}/${timestamp}_${safeName}`;

      const { error: storageError } = await supabase.storage
        .from('case-documents')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (storageError) throw storageError;

      const { data: dbData, error: dbError } = await supabase
        .from('case_documents')
        .insert({
          client_id: clientId,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          file_type: file.type,
          description: categoryKey,
        })
        .select()
        .single();

      if (dbError) throw dbError;

      const newFile: UploadedFile = {
        id: dbData.id,
        file_name: dbData.file_name,
        file_size: dbData.file_size,
        uploaded_at: dbData.uploaded_at,
      };

      setUploadedMap((prev) => {
        const updated = { ...prev, [slotId]: newFile };
        const allDone = SLOTS.every((s) => updated[s.id] !== null);
        if (allDone && !alreadySubmitted) {
          markClientComplete();
        }
        return updated;
      });
    } catch (err: unknown) {
      setErrors((prev) => ({
        ...prev,
        [slotId]: err instanceof Error ? err.message : 'Upload failed. Please try again.',
      }));
    } finally {
      setUploading(null);
      if (fileInputRefs.current[slotId]) {
        fileInputRefs.current[slotId]!.value = '';
      }
    }
  };

  const markClientComplete = async () => {
    if (markingDone) return;
    setMarkingDone(true);
    try {
      const submittedAt = new Date().toISOString();
      const { error } = await supabase
        .from('clients')
        .update({ cbp_docs_submitted_at: submittedAt })
        .eq('id', clientId);

      if (!error) {
        onAllSubmitted(submittedAt);

        // Auto-post milestone case note
        await supabase.from('case_notes').insert({
          client_id: clientId,
          note: 'The required CBP document has been received and is on file:\n\n• CBP Form 7501 (Entry Summary)\n\nYour case is now cleared to advance to the Filing Preparation stage. Your attorney will review the document and be in touch shortly.',
          note_type: 'milestone',
          created_by: 'system',
          is_visible: true,
        });

        // Fetch client info for notifications
        let clientRow: { name: string; email: string; company: string | null } | null = null;
        try {
          const { data } = await supabase
            .from('clients')
            .select('name, email, company')
            .eq('id', clientId)
            .maybeSingle();
          clientRow = data;
        } catch { /* non-fatal */ }

        // Notify admin
        if (clientRow) {
          try {
            await supabase.functions.invoke('send-admin-notification', {
              body: {
                eventType: 'cbp_docs_received',
                clientName: clientRow.name,
                clientEmail: clientRow.email,
                company: clientRow.company ?? undefined,
                timestamp: submittedAt,
              },
            });
          } catch (notifErr) {
            console.error('Admin notification error (non-fatal):', notifErr);
          }

          // Send client confirmation email listing received docs
          try {
            await supabase.functions.invoke('send-client-notification', {
              body: {
                eventType: 'cbp_docs_received',
                clientName: clientRow.name,
                clientEmail: clientRow.email,
                timestamp: submittedAt,
              },
            });
          } catch (emailErr) {
            console.error('Client CBP docs email (non-fatal):', emailErr);
          }
        }
      }
    } catch (err) {
      console.error('Failed to mark CBP docs complete:', err);
    } finally {
      setMarkingDone(false);
    }
  };

  const completedCount = SLOTS.filter((s) => uploadedMap[s.id] !== null).length;
  const allDone = completedCount === SLOTS.length || alreadySubmitted;

  return (
    <div className={`backdrop-blur-md rounded-2xl overflow-hidden border-2 transition-all ${allDone ? 'bg-green-500/10 border-green-500/40' : 'bg-white/5 border-white/10'}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-6 py-5 flex items-center justify-between gap-4 cursor-pointer hover:bg-white/5 transition-all"
      >
        <div className="flex items-center gap-4">
          <div className={`w-11 h-11 flex items-center justify-center rounded-xl border flex-shrink-0 ${allDone ? 'bg-green-500/20 border-green-500/40' : 'bg-[#FDB913]/20 border-[#FDB913]/40'}`}>
            <i className={`text-xl ${allDone ? 'ri-checkbox-circle-fill text-green-400' : 'ri-folder-open-line text-[#FDB913]'}`}></i>
          </div>
          <div className="text-left">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-base font-black text-white">Required CBP Documentation</h3>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-black border whitespace-nowrap ${
                allDone
                  ? 'bg-green-500/20 border-green-500/40 text-green-400'
                  : completedCount > 0
                  ? 'bg-[#FDB913]/20 border-[#FDB913]/40 text-[#FDB913]'
                  : 'bg-white/5 border-white/10 text-white/40'
              }`}>
                {allDone ? 'Complete' : `${completedCount}/${SLOTS.length} Uploaded`}
              </span>
            </div>
            <p className="text-white/50 text-xs mt-0.5">
              {allDone
                ? 'CBP Form 7501 has been received'
                : 'Upload CBP Form 7501 to proceed with your tariff refund filing'}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-white/5 rounded-lg">
          <i className={`text-white/50 text-sm transition-transform duration-200 ${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
        </div>
      </button>

      {/* Progress bar */}
      {!allDone && (
        <div className="px-6 pb-3">
          <div className="w-full bg-white/10 rounded-full h-1">
            <div
              className="h-1 rounded-full bg-gradient-to-r from-[#FDB913] to-[#e6a800] transition-all duration-700"
              style={{ width: `${(completedCount / SLOTS.length) * 100}%` }}
            />
          </div>
        </div>
      )}

      {expanded && (
        <div className="px-6 pb-6">
          {allDone ? (
            <div className="flex items-center gap-4 bg-green-500/10 border border-green-500/30 rounded-xl p-5">
              <div className="w-12 h-12 flex items-center justify-center bg-green-500/20 rounded-xl border border-green-500/40 flex-shrink-0">
                <i className="ri-checkbox-circle-fill text-green-400 text-2xl"></i>
              </div>
              <div>
                <p className="text-green-400 font-black text-base">All Documents Received</p>
                <p className="text-white/50 text-sm mt-0.5">Your attorney has the CBP Form 7501 needed to proceed with your tariff refund filing.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {SLOTS.map((slot) => {
                const uploaded = uploadedMap[slot.id];
                const isUploading = uploading === slot.id;
                const errMsg = errors[slot.id] || '';

                return (
                  <div
                    key={slot.id}
                    className={`rounded-xl border-2 transition-all overflow-hidden ${
                      uploaded
                        ? 'border-green-500/40 bg-green-500/5'
                        : 'border-white/10 bg-white/5'
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Status dot */}
                        <div className={`w-10 h-10 flex items-center justify-center rounded-xl border flex-shrink-0 mt-0.5 ${uploaded ? 'bg-green-500/20 border-green-500/40' : 'bg-white/5 border-white/10'}`}>
                          {uploaded
                            ? <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
                            : <i className={`${slot.icon} text-white/40 text-lg`}></i>
                          }
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                              <p className={`text-sm font-black ${uploaded ? 'text-white' : 'text-white/70'}`}>
                                {slot.label}
                              </p>
                              <p className="text-white/40 text-xs mt-0.5 leading-relaxed">{slot.detail}</p>
                            </div>

                            {!uploaded && (
                              <button
                                onClick={() => fileInputRefs.current[slot.id]?.click()}
                                disabled={isUploading}
                                className="flex items-center gap-2 bg-[#FDB913]/10 hover:bg-[#FDB913]/20 border border-[#FDB913]/40 text-[#FDB913] font-bold text-xs px-4 py-2 rounded-lg transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 flex-shrink-0"
                              >
                                {isUploading
                                  ? <><i className="ri-loader-4-line animate-spin"></i> Uploading...</>
                                  : <><i className="ri-upload-2-line"></i> Upload</>
                                }
                              </button>
                            )}
                          </div>

                          {uploaded && (
                            <div className="mt-2 flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2">
                              <i className="ri-file-check-line text-green-400 text-sm flex-shrink-0"></i>
                              <div className="min-w-0 flex-1">
                                <p className="text-green-300 text-xs font-bold truncate">{uploaded.file_name}</p>
                                <p className="text-white/30 text-xs">{formatFileSize(uploaded.file_size)} &bull; {new Date(uploaded.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                              </div>
                              <button
                                onClick={() => fileInputRefs.current[slot.id]?.click()}
                                className="text-white/30 hover:text-white/60 text-xs font-bold whitespace-nowrap cursor-pointer flex-shrink-0 transition-colors"
                                title="Replace file"
                              >
                                Replace
                              </button>
                            </div>
                          )}

                          {errMsg && (
                            <p className="mt-2 text-red-400 text-xs flex items-center gap-1">
                              <i className="ri-error-warning-line"></i>{errMsg}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <input
                      ref={(el) => { fileInputRefs.current[slot.id] = el; }}
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.txt"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleFileChange(slot.id, slot.categoryKey, f);
                      }}
                    />
                  </div>
                );
              })}

              {completedCount === SLOTS.length && !alreadySubmitted && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-green-400 text-xl flex-shrink-0"></i>
                  <p className="text-green-300 text-sm font-bold">
                    CBP Form 7501 uploaded — your attorney has been notified and will proceed with your filing.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
